// =============================================================================
//  Copyright : 2022 semantics GmbH
//  Date      : $Date: 2024-01-10 01:14:07 +0100 (Wed, 10 Jan 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 139968 $
//  Purpose   : frontend hooks
// =============================================================================
// ---Third-party imports
import React from 'react'

import {Loader, Divider} from 'semantic-ui-react'
import {matchSorter} from 'match-sorter'

// ---Framework imports
import {
  get,
  size,
  isDigit,
  isString,
  sorted,
  dict,
  isArray,
  keys,
  isNil,
  values,
  reversed,
} from 'ldash'
import {Actions, Sel} from 'uix/registries'
import log from 'uix/logging'

import {hashObj} from 'uix/utils'
import {normalize} from 'uix/normalize'
import {
  useEffect,
  useSelector,
  useActions,
  useAction,
  useApi,
  useInView,
  useState,
  useResettableState,
  useMemo,
  useRouter,
  useCallback,
  usePhysicalNode,
} from 'uix/hooks'
import {useTreeList} from 'uix/db/hooks'
// ---Local imports

// ---Globals
const MAX_TITLES = 20

// ---Exports
const ScrollIntoViewLoader = ({onInView, active}) => {
  let [ref, onScreen] = useInView({triggerOnce: false})

  useEffect(() => {
    if (onScreen && active) {
      onInView()
    }
  }, [onScreen, active])

  return (
    <Divider horizontal>
      <span ref={ref}>
        <Loader active indeterminate inline size="small" />
      </span>
    </Divider>
  )
}
const normalizeRestriction = licence =>
  licence
    ? {
        id: licence?.licid,
        name: licence?.licname,
        allowed: licence?.allowed && licence?.allowed !== 'false',
        vlreader: licence?.vlreader && licence?.vlreader !== 'false',
        reason: licence?.reason,
        descendant: licence?.descendantrestrictions,
      }
    : {allowed: true}

const buildMiniTitleInfoData = ({otid, odid, datatype, subtype, type, caption, ...r}) => ({
  otid,
  odid,
  datatype,
  subtype,
  type,
  caption,
  released: r.ot_state, // 0, 1, 2
  textstate: r.oc_textstate, // 0, 1
  page: {otid: r.op_ot_id, width: r.op_imgwidth, height: r.op_imgheight},
  restriction: normalizeRestriction(r.licence),
  mods: r.mods,
  data: r,
})

export const useMinititleInfos = otids => {
  let returnSingle = null

  if (!isArray(otids)) {
    returnSingle = otids
    otids = [otids]
  }

  let titleDetails = useSelector(root => root.data.titleDetails)
  let actions = useActions({reduceApi: Actions.reduceApi})

  let [loadingIds, setLoadingIds] = useState({})
  let missingDetails = otids
    .filter(otid => !titleDetails || !titleDetails[otid])
    .filter(otid => otid)
    .filter(otid => isNil(loadingIds[otid])) // ignore true + false

  let getMiniTitleinfos = (dataState, data, error) => {
    let details = dict(
      normalize(data.titles, {format: 'raw'}).map(row => [row.otid, buildMiniTitleInfoData(row)])
    )
    return {titleDetails: {...dataState.titleDetails, ...details}}
  }

  useEffect(() => {
    if (missingDetails.length > 0) {
      let ids = missingDetails.join(',')
      setLoadingIds({...loadingIds, ...dict(missingDetails.map(otid => [otid, true]))})
      actions.reduceApi('getMiniTitleinfos', {ids}, getMiniTitleinfos, {
        method: 'post',
      })
    }
  }, [hashObj(missingDetails)])

  let res = titleDetails || {}
  return returnSingle ? res[returnSingle] : res
}

const normalizeFilterResult = titles =>
  titles.map(
    ({
      OT_CAPTION,
      OC_TEXTSTATE,
      OT_RCV_DATATYPE,
      OP_OT_ID,
      OT_RCV_TYPE,
      OC_SIGNATURE,
      OC_RELEASEDATE,
      OC_PROVIDER,
      OT_STATE,
      OP_IMGHEIGHT,
      OP_IMGWIDTH,
      OC_MPMODE,
      OT_ID,
      OT_OD_ID,
      type,
      licence,
      mods,
    }) => ({
      odid: OT_OD_ID,
      otid: OT_ID,
      datatype: OT_RCV_DATATYPE,
      subtype: OT_RCV_TYPE,
      typeName: type,
      released: OT_STATE,
      mpmode: OC_MPMODE,
      caption: OT_CAPTION,
      textstate: OC_TEXTSTATE,
      page: {otid: OP_OT_ID, width: OP_IMGWIDTH, height: OP_IMGHEIGHT},
      restriction: normalizeRestriction(licence),
      mods,
    })
  )
const useApiFacetCached = (api_name, api_params, api_opts, facetted) => {
  /* wrap useApi with facet caching
  XXX: this seems to be too complicated
   */
  // every query param that affects output list needs to be registered here
  let facet_data_key = hashObj([api_name, api_params.facets])
  // store collected
  let [collected_facet_data, set_collected_facet_data] = useResettableState({}, [facetted])
  let [facet_data_initiator, set_facet_data_initiator] = useResettableState({}, [facetted])
  let facet_data = collected_facet_data[facet_data_key]
  let facets_loaded = !!facet_data

  // keep track of api_params that initiate a facetted request
  let api_params_hash = hashObj([api_name, api_params])

  if (facets_loaded && api_params_hash !== facet_data_initiator[facet_data_key]) {
    // disable facets on API if
    // 1) facets are loaded
    // 2) and this api_params set was not responsible for loading the facets
    api_params = {...api_params, nof: true}
  }
  let valid_facets = f => size(f) > 1
  let {data, error, loading} = useApi(api_name, api_params, api_opts)
  useEffect(() => {
    if (valid_facets(data?.facets)) {
      set_collected_facet_data({...collected_facet_data, [facet_data_key]: data.facets})
      if (!facet_data_initiator[facet_data_key]) {
        set_facet_data_initiator({...facet_data_initiator, [facet_data_key]: api_params_hash})
      }
    }
  }, [data])

  data = useMemo(() => {
    if (!data) return data
    let facets = valid_facets(data.facets) ? data.facets : collected_facet_data[facet_data_key]
    if (!facetted) {
      facets = {}
    }
    return {...data, facets}
  }, [data, collected_facet_data, facet_data_key, facetted])
  return {data, error, loading}
}

export const useOffsetNavigation = () => {
  /* control routing for &offset, &max, &s, &o query params
   */
  let {request, go} = useRouter()
  let sortAvailable = ['title', 'name', 'place', 'date']
  let defaults = {sort: 'title', order: 'asc', max: 10, offset: 0}
  let {
    offset = 0,
    max = defaults.max,
    s: sort = defaults.sort,
    o: order = defaults.order,
  } = request.query

  let build_params = (n, a, v) => ({offset: null, [n]: defaults[a || n] == v ? null : v})

  let _go = (name, alias) => v => go({updateQuery: build_params(name, alias, v)})

  let setter = useMemo(
    () => ({
      setOffset: _go('offset'),
      setMax: _go('max'),
      setSort: _go('s', 'sort'),
      setOrder: _go('o', 'order'),
    }),
    [go]
  )

  return {offset, max, sort, order, sortAvailable, ...setter}
}

export const useIndexList = ({
  offset = 0,
  max = 10,
  order = 'asc',
  sort,
  root_otid,
  enabled = true,
  facetted = false,
} = {}) => {
  /* fetch slices (offset / max) of titledata lists (datatype=metdata) from index
   */
  //~ log.warn('xxx %o', {facetted})

  let api_name = 'filter/getAllDocuments'
  let list_key = 'allDocuments'
  let api_params = {offset, max, o: order, s: sort}

  if (root_otid) {
    api_name = 'filter/getContentChildren'
    api_params = {...api_params, id: root_otid}
    list_key = 'titles'
  }

  let request = useSelector(Sel.request)
  let q_facets = request.query.facets
  if (!facetted) {
    api_params = {...api_params, nof: true}
  }

  if (q_facets && facetted) {
    api_params = {...api_params, facets: q_facets}
  }

  let {data, error, loading} = useApiFacetCached(
    api_name,
    api_params,
    {
      condition: enabled,
      // no empty result (after loading) if condition is false
      conditionNullResult: false,
      autoreset: false,
      //~ deps: [facetted]
    },
    facetted
  )
  let {titles, facets, navigator} = useMemo(() => {
    let titles = []
    let facets = {}
    let navigator = {total: 0, offset: 0, max: 0}
    if (data) {
      titles = normalizeFilterResult(data[list_key] || [])
      facets = data.facets
      navigator = data.navigator
    }
    return {titles, facets, navigator}
  }, [data])

  return {titles, facets, navigator, loading}
}

export const useIndexListExpanding = ({
  limit,
  enabled = true,
  load_count = 10,
  facetted,
  ...listParams
}) => {
  /* fetch titledata (datatype=metdata) from index
  increasing limit will fetch missing data
   */

  let {request} = useRouter()
  let {facets: q_facets, sort, order} = request.query
  let reset_dep = [q_facets, sort, order, facetted]
  let [loadedListData, setLoadedListData] = useResettableState(
    {num_loaded: 0, titles: []},
    reset_dep
  )
  let [scrolled, setScrolled] = useResettableState(0, reset_dep)

  let offset = loadedListData.num_loaded
  let needed = limit + scrolled
  let max = needed - offset
  let api_idle = max == 0

  enabled = enabled && !api_idle
  let {titles, facets, navigator, loading} = useIndexList({
    ...listParams,
    enabled,
    offset,
    max,
    facetted,
  })

  useEffect(() => {
    let num_loaded = navigator.offset + navigator.max
    if (num_loaded > loadedListData.num_loaded) {
      titles = [...loadedListData.titles, ...titles]
      setLoadedListData({num_loaded, titles})
    }
  }, [navigator, titles])

  let loaderVisible = navigator.total > 0

  let LoadMoreIfRendered =
    loaderVisible && api_idle ? (
      <ScrollIntoViewLoader onInView={() => setScrolled(scrolled + load_count)} active={!loading} />
    ) : null
  return {
    loading,
    facets,
    total: navigator.total,
    titles: loadedListData.titles,
    LoadMoreIfRendered,
  }
}

export const useTitleList = ({subdomains, limit, source = 'db', searchTerm}) => {
  /* fetch titles either from db or from index
   */
  let request = useSelector(Sel.request)
  let ancestorAxis = useSelector(Sel.ancestorAxis)

  let db_params = {
    parentid: 0,
    typeName: 'metadata',
    descendants: true,
    subdomains,
    dname: request.dname,
  }
  let list_otid = null

  if (isDigit(request.id)) {
    let topstructs = ancestorAxis.filter(e => e.datatype == 2)
    if (topstructs.length > 0) {
      list_otid = topstructs[0].otid
      db_params = {parentid: list_otid, descendants: true, typeName: 'metadata'}
    }
  } else if (isString(request.id)) {
    if (request.id.includes('~byState~')) {
      db_params.stateNames = request.id
        .split('~')
        .slice(-2)
        .join(':')
    }
    if (request.id.includes('~byType~')) {
      db_params.typeName = request.id.split('~').slice(-1)[0]
    }
  }
  let facets = null
  let {treeList, loading} = useTreeList('titleList', db_params, true, source == 'db')
  let db_titles = treeList.list
  let total = db_titles?.length || 0

  let [filteredTitles, setFilteredTtitles] = useState([])

  useEffect(() => {
    if (db_titles && searchTerm) {
      let t = matchSorter(db_titles, searchTerm, {keys: ['caption']})
      setFilteredTtitles(t)
    } else {
      setFilteredTtitles([])
    }
  }, [source, searchTerm, db_titles])

  let titles = db_titles
  if (source == 'db' && searchTerm) {
    titles = filteredTitles
  }

  let [offset, setOffset] = useState(0)
  useEffect(() => {
    setOffset(0)
  }, [list_otid, searchTerm, source])

  let num_active = limit + offset

  let index_titles = useIndexList({
    limit: num_active,
    root_otid: list_otid,
    enabled: source.startsWith('index'),
  })
  if (index_titles) {
    titles = index_titles.titles
    total = index_titles.total
    loading = index_titles.loading
    facets = index_titles.facets
  }
  let loaderVisible = num_active < total
  let LoadMoreIfRendered = loaderVisible ? (
    <ScrollIntoViewLoader onInView={() => setOffset(offset + 5)} active={!loading} />
  ) : null
  titles = (titles || []).slice(0, num_active)

  return {list_otid, titles, facets, total, loading, LoadMoreIfRendered}
}

export const useTopStructures = ({
  root_otid = null,
  firstLevel = false,
  secondLevel = false,
  nextLevel = false,
} = {}) => {
  /* fetch getFirstClassificationLevel and/or getClassificationChildAxis
   */
  let {data: data1, loading: loading1} = useApi(
    'filter/getFirstClassificationLevel',
    {secondLevel},
    {autoreset: false, condition: firstLevel || secondLevel, cached: true}
  )

  let firstClassificationLevel = useMemo(() => {
    if (!data1) return []
    let build = children =>
      children.map(e => ({
        otid: parseInt(e.ot_id),
        caption: e.ot_caption,
        odid: parseInt(e.ot_od_id),
        datatype: parseInt(e.ot_rcv_datatype),
        subtype: parseInt(e.ot_rcv_type),
        released: parseInt(e.ot_state),
        has_childstructs: e.substructavailable == 'true',
        titlepagebox_otid: e.titlepagebox_otid ? parseInt(e.titlepagebox_otid) : null,
        children: build(e.$children || []),
      }))

    return build(data1.firstClassificationLevel.$children || [])
  }, [data1])

  let {data: dataN, loading: loadingN} = useApi(
    'filter/getClassificationChildAxis',
    {id: root_otid, structonly: true},
    {autoreset: false, condition: nextLevel && !!root_otid, cached: true}
  )

  let nextClassificationLevel = useMemo(() => {
    if (!dataN?.structChildAxis?.$children) return []
    let build = children =>
      children.map(e => ({
        otid: parseInt(e.ot_id),
        caption: e.ot_caption,
        odid: parseInt(e.ot_od_id),
        datatype: parseInt(e.ot_rcv_datatype),
        subtype: parseInt(e.ot_rcv_type),
        released: parseInt(e.ot_state),
        has_children: e.substructavailable == 'true',
        titlepagebox_otid: e.titlepagebox_otid ? parseInt(e.titlepagebox_otid) : null,
        children: build(e.$children || []),
      }))

    return build(dataN.structChildAxis.$children)
  }, [dataN])

  return {firstClassificationLevel, nextClassificationLevel, loading: loading1 || loadingN}
}

export const useAncestorTree = ({firstLevel = false, secondLevel = false, nextLevel = false}) => {
  /* combine ancestorAxis with data from useTopStructures
   */
  let node = usePhysicalNode()
  let root_otid = node.datatype == 2 ? node.otid : null
  let {firstClassificationLevel, nextClassificationLevel, loading} = useTopStructures({
    root_otid,
    firstLevel,
    secondLevel,
    nextLevel,
  })
  let ancestorTree = useMemo(() => {
    let children = nextClassificationLevel || []
    let ancestorTree = reversed(node.ancestors || []).reduce(
      (curr, n) => ({...n, children: [curr]}),
      {
        ...node,
        children,
      }
    )

    if (size(firstClassificationLevel)) {
      return firstClassificationLevel.map(n =>
        n.otid == ancestorTree.otid ? {...n, ...ancestorTree} : n
      )
    }
    return [ancestorTree]
  }, [node, firstClassificationLevel, nextClassificationLevel])
  return {ancestorTree, loading}
}
